import React, { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import {
  Globe, Clock, Bell, Palette, MapPin, Phone, Mail,
  Building2, CreditCard, ExternalLink, CheckCircle2, XCircle, Save,
  CalendarClock, Trash2, Plus, Copy, CalendarOff, AlertTriangle,
} from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import { useLanguage } from '../lib/LanguageContext'

const TABS = [
  { key: 'practice', label: 'settings_tab_practice', labelAf: 'Praktyk-inligting', icon: Building2 },
  { key: 'hours',    label: 'settings_tab_hours',    labelAf: 'Kantoorure',        icon: Clock },
  { key: 'notifications', label: 'settings_tab_notifications', labelAf: 'Kennisgewings', icon: Bell },
  { key: 'appearance',    label: 'settings_tab_appearance',    labelAf: 'Voorkoms',       icon: Palette },
]

export default function Settings({ schedules, pricing, practice, billing, notifications, ai_costs, office_hours }) {
  const { t, language, setLanguage } = useLanguage()
  const [activeTab, setActiveTab] = useState('practice')

  return (
    <DashboardLayout>
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary/10">
            <Building2 size={20} className="text-brand-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-brand-ink">{t('settings_title')}</h1>
        {ai_costs?.last_7_days && (
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm mb-4">
            <h2 className="text-sm font-semibold text-brand-ink mb-2">🤖 AI usage (last 7 days)</h2>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><div className="text-gray-500">Calls</div><div className="text-lg font-semibold">{ai_costs.last_7_days.calls}</div></div>
              <div><div className="text-gray-500">Tokens</div><div className="text-lg font-semibold">{(ai_costs.last_7_days.input_tokens + ai_costs.last_7_days.output_tokens).toLocaleString()}</div></div>
              <div><div className="text-gray-500">Cost (USD)</div><div className="text-lg font-semibold">${Number(ai_costs.last_7_days.cost_usd).toFixed(4)}</div></div>
            </div>
            {ai_costs.today && (
              <div className="mt-3 text-xs text-gray-600">Today: {ai_costs.today.calls} call{ai_costs.today.calls === 1 ? '' : 's'}, ${Number(ai_costs.today.cost_usd).toFixed(4)}</div>
            )}
          </div>
        )}
            <p className="text-sm text-brand-muted">{t('settings_subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-brand-border mb-6">
        <nav className="flex gap-0" aria-label="Settings tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-brand-muted hover:text-brand-ink hover:border-brand-border'
              }`}
            >
              <Icon size={16} />
              {t(label)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="max-w-4xl">
        {activeTab === 'practice' && <PracticeTab practice={practice} pricing={pricing} billing={billing} t={t} />}
        {activeTab === 'hours' && (office_hours ? <OfficeHoursTab office_hours={office_hours} t={t} /> : <HoursTab schedules={schedules} t={t} />)}
        {activeTab === 'notifications' && <NotificationsTab notifications={notifications} t={t} />}
        {activeTab === 'appearance' && <AppearanceTab language={language} setLanguage={setLanguage} t={t} />}
      </div>
    </DashboardLayout>
  )
}

/* ── Practice Info Tab ──────────────────────────────────────────────── */
function PracticeTab({ practice, pricing, billing = {}, t }) {
  const [practiceForm, setPracticeForm] = useState({ ...practice })
  const [pricingForm, setPricingForm] = useState({ ...pricing })
  const [billingForm, setBillingForm] = useState({ ...billing })
  const [savingPractice, setSavingPractice] = useState(false)
  const [savingPricing, setSavingPricing] = useState(false)
  const [savingBilling, setSavingBilling] = useState(false)

  const saveBilling = (e) => {
    e.preventDefault()
    setSavingBilling(true)
    router.patch('/settings/billing', { billing: billingForm }, {
      preserveScroll: true,
      onSuccess: () => toast.success('Billing details saved'),
      onError: () => toast.error('Failed to save billing details'),
      onFinish: () => setSavingBilling(false),
    })
  }
  const billingField = (key, label, placeholder) => (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold uppercase tracking-wide text-brand-muted">{label}</label>
      <input type="text" value={billingForm[key] || ''} placeholder={placeholder}
        onChange={e => setBillingForm(f => ({ ...f, [key]: e.target.value }))}
        className="h-9 rounded-lg border border-brand-border bg-brand-surface px-3 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
    </div>
  )

  const savePractice = (e) => {
    e.preventDefault()
    setSavingPractice(true)
    router.patch('/settings/practice', { practice: practiceForm }, {
      preserveScroll: true,
      onSuccess: () => toast.success('Practice details saved'),
      onError: () => toast.error('Failed to save practice details'),
      onFinish: () => setSavingPractice(false),
    })
  }

  const savePricing = (e) => {
    e.preventDefault()
    setSavingPricing(true)
    router.patch('/settings/pricing', {
      pricing: {
        price_consultation: pricingForm.consultation,
        price_check_up:     pricingForm.check_up,
        price_cleaning:     pricingForm.cleaning,
      }
    }, {
      preserveScroll: true,
      onSuccess: () => toast.success('Pricing saved'),
      onError: () => toast.error('Failed to save pricing'),
      onFinish: () => setSavingPricing(false),
    })
  }

  const practiceField = (key, label, placeholder, icon) => (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-brand-muted uppercase tracking-wide">
        {icon && React.createElement(icon, { size: 12, className: 'inline mr-1 text-brand-muted' })}
        {label}
      </label>
      <input
        type="text"
        value={practiceForm[key] || ''}
        onChange={e => setPracticeForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        className="h-9 rounded-lg border border-brand-border bg-brand-surface px-3 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
      />
    </div>
  )

  const pricingField = (key, label) => (
    <div className="flex items-center gap-3">
      <label className="w-36 text-sm font-medium text-brand-ink capitalize">{label}</label>
      <input
        type="text"
        value={pricingForm[key] || ''}
        onChange={e => setPricingForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder="e.g. approximately R850"
        className="flex-1 h-9 rounded-lg border border-brand-border bg-brand-surface px-3 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
      />
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Practice Details */}
      <Card
        icon={Building2}
        title={t('settings_practice_info')}
        subtitle={t('settings_practice_info_desc')}
      >
        <form onSubmit={savePractice} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {practiceField('name', t('settings_practice_name'), 'Practice name')}
            {practiceField('phone', t('settings_practice_phone'), '+27 XX XXX XXXX', Phone)}
            {practiceField('email', t('settings_practice_email'), 'reception@example.com', Mail)}
            {practiceField('emergency_phone', 'Emergency phone', '071 XXX XXXX', Phone)}
            {practiceField('address_line1', 'Address line 1', 'Unit 2, Building Name', MapPin)}
            {practiceField('address_line2', 'Address line 2', 'Corner of Street A & Street B')}
            {practiceField('city', 'City / Suburb', 'Roodepoort, Johannesburg, 2040')}
            {practiceField('map_link', 'Google Maps link', 'https://maps.app.goo.gl/...')}
            {practiceField('google_review_url', 'Google review link (for review requests)', 'https://g.page/r/...')}
          </div>
          {practiceForm.map_link && (
            <a
              href={practiceForm.map_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-brand-primary hover:underline"
            >
              <MapPin size={14} />
              {t('settings_view_on_map')}
              <ExternalLink size={12} />
            </a>
          )}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={savingPractice}
              className="flex items-center gap-2 rounded-xl bg-brand-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-primary/90 disabled:opacity-50"
            >
              <Save size={15} />
              {savingPractice ? 'Saving…' : 'Save Practice Details'}
            </button>
          </div>
        </form>
      </Card>

      {/* Billing details — shown on invoices, statements & medical-aid claims */}
      <Card icon={Building2} title="Billing details (on invoices & statements)"
        subtitle="HPCSA, BHF/practice number, VAT and banking — used on every invoice, statement and medical-aid claim.">
        <form onSubmit={saveBilling} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {billingField('hpcsa_number', 'Practice HPCSA number', 'e.g. MP0123456')}
            {billingField('bhf_practice_number', 'BHF / practice number', 'e.g. 0992801')}
            {billingField('vat_number', 'VAT number', 'e.g. 4XXXXXXXXX')}
            {billingField('company_reg', 'Company registration', 'e.g. 2021/XXXXXX/21')}
            {billingField('practitioner_name', 'Treating practitioner name', 'Dr Chalita le Roux')}
            {billingField('practitioner_hpcsa_number', 'Practitioner HPCSA', 'e.g. DP0118702')}
            {billingField('bank_name', 'Bank name', 'e.g. FNB')}
            {billingField('bank_account_name', 'Account name', 'Dr Chalita le Roux Inc')}
            {billingField('bank_account_number', 'Account number', '')}
            {billingField('bank_branch_code', 'Branch code', '')}
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={savingBilling}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white hover:bg-brand-primary-dark disabled:opacity-50">
              {savingBilling ? 'Saving…' : 'Save Billing Details'}
            </button>
          </div>
        </form>
      </Card>

      {/* Pricing */}
      <Card
        icon={CreditCard}
        title={t('settings_pricing')}
        subtitle={t('settings_pricing_desc')}
      >
        <form onSubmit={savePricing} className="space-y-3">
          {pricingField('consultation', 'Consultation')}
          {pricingField('check_up', 'Check-up')}
          {pricingField('cleaning', 'Cleaning')}

          <div className="flex items-center justify-between pt-1">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex-1 mr-4">
              <p className="text-xs text-amber-800">{t('settings_cash_practice_note')}</p>
            </div>
            <button
              type="submit"
              disabled={savingPricing}
              className="flex items-center gap-2 rounded-xl bg-brand-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-primary/90 disabled:opacity-50"
            >
              <Save size={15} />
              {savingPricing ? 'Saving…' : 'Save Pricing'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  )
}

/* ── Office Hours Tab ───────────────────────────────────────────────── */
function HoursTab({ schedules, t }) {
  return (
    <div className="space-y-6">
      <Card
        icon={Clock}
        title={t('settings_office_hours')}
        subtitle={t('settings_office_hours_desc')}
      >
        <div className="overflow-hidden rounded-xl border border-brand-border">
          <table className="min-w-full">
            <thead>
              <tr className="bg-brand-surface">
                <th className="text-left px-5 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wide">{t('settings_day')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wide">{t('settings_hours')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wide">{t('settings_break')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wide">{t('settings_status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border bg-white">
              {schedules?.map((schedule) => (
                <tr key={schedule.id} className="hover:bg-brand-surface/50 transition-colors">
                  <td className="px-5 py-3.5 text-sm font-medium text-brand-ink capitalize">{schedule.day_name}</td>
                  <td className="px-5 py-3.5 text-sm text-brand-muted">
                    {schedule.active ? `${schedule.start_time} — ${schedule.end_time}` : '—'}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-brand-muted">
                    {schedule.break_start && schedule.break_end
                      ? `${schedule.break_start} — ${schedule.break_end}`
                      : '—'}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                      schedule.active
                        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                        : 'bg-gray-50 text-gray-500 ring-1 ring-gray-200'
                    }`}>
                      {schedule.active
                        ? <><CheckCircle2 size={12} /> {t('settings_open')}</>
                        : <><XCircle size={12} /> {t('settings_closed')}</>
                      }
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

/* ── Notifications Tab ──────────────────────────────────────────────── */
function NotificationsTab({ notifications, t }) {
  const channels = [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      confirmations: notifications?.whatsapp_confirmations,
      reminders: notifications?.whatsapp_reminders,
      color: 'emerald',
    },
    {
      key: 'email',
      label: t('settings_notif_email'),
      confirmations: notifications?.email_confirmations,
      reminders: notifications?.email_reminders,
      color: 'blue',
    },
    {
      key: 'sms',
      label: 'SMS',
      confirmations: notifications?.sms_confirmations,
      reminders: notifications?.sms_reminders,
      color: 'violet',
    },
  ]

  return (
    <div className="space-y-6">
      <Card
        icon={Bell}
        title={t('settings_notif_title')}
        subtitle={t('settings_notif_subtitle')}
      >
        <div className="space-y-4">
          {channels.map(({ key, label, confirmations, reminders, color }) => (
            <div key={key} className="flex items-center justify-between rounded-xl border border-brand-border p-4 hover:bg-brand-surface/30 transition-colors">
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-${color}-50`}>
                  {key === 'whatsapp' && <Phone size={18} className={`text-${color}-600`} />}
                  {key === 'email' && <Mail size={18} className={`text-${color}-600`} />}
                  {key === 'sms' && <Phone size={18} className={`text-${color}-600`} />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-brand-ink">{label}</p>
                  <p className="text-xs text-brand-muted">
                    {confirmations && reminders
                      ? t('settings_notif_both_active')
                      : confirmations
                        ? t('settings_notif_confirm_only')
                        : reminders
                          ? t('settings_notif_remind_only')
                          : t('settings_notif_not_configured')
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusDot active={confirmations} label={t('settings_notif_confirmations')} />
                <StatusDot active={reminders} label={t('settings_notif_reminders')} />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 rounded-lg bg-brand-surface p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-brand-ink">{t('settings_notif_reminder_timing')}</p>
              <p className="text-xs text-brand-muted">{t('settings_notif_reminder_timing_desc')}</p>
            </div>
            <span className="rounded-lg bg-white border border-brand-border px-4 py-2 text-sm font-semibold text-brand-ink">
              {notifications?.reminder_hours_before || 24}h
            </span>
          </div>
        </div>
      </Card>
    </div>
  )
}

/* ── Appearance Tab ─────────────────────────────────────────────────── */
function AppearanceTab({ language, setLanguage, t }) {
  return (
    <div className="space-y-6">
      {/* Language */}
      <Card
        icon={Globe}
        title={t('settings_language')}
        subtitle={t('settings_language_desc')}
      >
        <div className="flex items-center gap-2 rounded-xl border border-brand-border bg-brand-surface p-1.5 w-fit">
          <button
            onClick={() => setLanguage('en')}
            className={`rounded-lg px-5 py-2.5 text-sm font-medium transition-all ${
              language === 'en'
                ? 'bg-white text-brand-ink shadow-sm ring-1 ring-brand-border'
                : 'text-brand-muted hover:text-brand-ink'
            }`}
          >
            English
          </button>
          <button
            onClick={() => setLanguage('af')}
            className={`rounded-lg px-5 py-2.5 text-sm font-medium transition-all ${
              language === 'af'
                ? 'bg-white text-brand-ink shadow-sm ring-1 ring-brand-border'
                : 'text-brand-muted hover:text-brand-ink'
            }`}
          >
            Afrikaans
          </button>
        </div>
      </Card>
    </div>
  )
}

/* ── Shared Components ──────────────────────────────────────────────── */
function Card({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-white rounded-xl border border-brand-border shadow-sm">
      <div className="flex items-center gap-3 px-6 py-5 border-b border-brand-border">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-primary/10">
          <Icon size={18} className="text-brand-primary" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-brand-ink">{title}</h2>
          {subtitle && <p className="text-xs text-brand-muted mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="px-6 py-5">
        {children}
      </div>
    </div>
  )
}


function StatusDot({ active, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`h-2 w-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
      <span className="text-xs text-brand-muted">{label}</span>
    </div>
  )
}

// Per-provider Office Hours editor: weekly work/break template (effective-dated), copy-week,
// leave/holidays and one-off custom days. Writes to /settings/office-hours/* (see SettingsController).
function OfficeHoursTab({ office_hours, t }) {
  const oh = office_hours || {}
  const providers = oh.providers || []
  const realProviders = providers.filter(p => p.id != null)
  const [providerId, setProviderId] = useState(oh.selected_provider_id ?? realProviders[0]?.id ?? null)

  const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]
  const DAY_NAMES = { 0: 'Sunday', 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday' }
  const tomorrowISO = () => new Date(Date.now() + 86400000).toISOString().slice(0, 10)
  const toMin = (x) => { const [h, m] = (x || '').split(':').map(Number); return (h || 0) * 60 + (m || 0) }

  const emptyWeek = () => ({ '0': [], '1': [], '2': [], '3': [], '4': [], '5': [], '6': [] })
  const cloneWeek = (sbd) => {
    const w = emptyWeek()
    Object.keys(w).forEach(d => {
      w[d] = ((sbd && sbd[d]) || []).map(s => ({ kind: s.kind, start_time: s.start_time, end_time: s.end_time, label: s.label || '' }))
    })
    return w
  }

  const activeVersion = oh.active_version
  const isEditingFuture = !!activeVersion?.editable
  const [week, setWeek] = useState(cloneWeek(activeVersion?.segments_by_day))
  const [effectiveFrom, setEffectiveFrom] = useState(isEditingFuture ? (activeVersion?.effective_from || tomorrowISO()) : tomorrowISO())
  const [note, setNote] = useState(activeVersion?.note || '')
  const [savingWeek, setSavingWeek] = useState(false)
  const [leaveForm, setLeaveForm] = useState({ provider_id: providerId, exception_type: 0, start_date: tomorrowISO(), end_date: tomorrowISO(), reason: '' })
  const [savingLeave, setSavingLeave] = useState(false)
  const [customForm, setCustomForm] = useState({ date: tomorrowISO(), reason: '', start_time: '09:00', end_time: '13:00' })
  const [savingCustom, setSavingCustom] = useState(false)
  const [copyTo, setCopyTo] = useState('')
  const [copyEff, setCopyEff] = useState(tomorrowISO())
  const [copying, setCopying] = useState(false)

  // Re-sync local editors when the selected provider's data changes (after an Inertia reload).
  useEffect(() => {
    setWeek(cloneWeek(oh.active_version?.segments_by_day))
    setEffectiveFrom(oh.active_version?.editable ? (oh.active_version?.effective_from || tomorrowISO()) : tomorrowISO())
    setNote(oh.active_version?.note || '')
    setLeaveForm(f => ({ ...f, provider_id: oh.selected_provider_id }))
  }, [oh.selected_provider_id, oh.active_version?.id])

  const opts = (flagSetter, okMsg) => ({
    preserveScroll: true, preserveState: true,
    onSuccess: () => toast.success(okMsg),
    onError: () => toast.error('Something went wrong'),
    onFinish: () => flagSetter(false),
  })
  const switchProvider = (id) => { setProviderId(id); router.reload({ only: ['office_hours'], data: { oh_provider_id: id }, preserveScroll: true }) }

  const dayHasWork = (d) => (week[String(d)] || []).some(s => s.kind === 0)
  const updateSeg = (d, i, field, val) => setWeek(w => ({ ...w, [d]: w[d].map((s, idx) => idx === i ? { ...s, [field]: val } : s) }))
  const addSeg = (d, kind) => setWeek(w => ({ ...w, [d]: [...w[d], { kind, start_time: kind === 0 ? '08:00' : '12:00', end_time: kind === 0 ? '17:00' : '12:30', label: '' }] }))
  const removeSeg = (d, i) => setWeek(w => ({ ...w, [d]: w[d].filter((_, idx) => idx !== i) }))

  const validateWeek = () => {
    for (const d of Object.keys(week)) {
      for (const s of week[d]) { if (!s.start_time || !s.end_time || toMin(s.end_time) <= toMin(s.start_time)) return 'Each time must end after it starts' }
      const works = week[d].filter(s => s.kind === 0).map(s => [toMin(s.start_time), toMin(s.end_time)]).sort((a, b) => a[0] - b[0])
      for (let i = 1; i < works.length; i++) if (works[i][0] < works[i - 1][1]) return 'Work blocks overlap on the same day'
      for (const s of week[d].filter(s => s.kind === 1)) { const bs = toMin(s.start_time), be = toMin(s.end_time); if (!works.some(([ws, we]) => bs >= ws && be <= we)) return 'A break falls outside working hours' }
    }
    return null
  }
  const flatten = () => {
    const out = []
    Object.keys(week).forEach(d => { if (week[d].some(s => s.kind === 0)) week[d].forEach(s => out.push({ day_of_week: Number(d), kind: s.kind, start_time: s.start_time, end_time: s.end_time, label: s.label })) })
    return out
  }
  const weekError = validateWeek()

  const saveWeek = () => {
    if (weekError) { toast.error(weekError); return }
    setSavingWeek(true)
    const payload = { version: { effective_from: effectiveFrom, timezone: 'Africa/Johannesburg', note, segments: flatten() } }
    if (isEditingFuture && activeVersion?.id) router.patch(`/settings/office-hours/versions/${activeVersion.id}`, payload, opts(setSavingWeek, 'Schedule updated'))
    else router.post(`/settings/office-hours/providers/${providerId}/versions`, payload, opts(setSavingWeek, 'Schedule updated'))
  }
  const addLeave = () => {
    if (!leaveForm.reason) { toast.error('Add a short reason'); return }
    setSavingLeave(true)
    router.post('/settings/office-hours/exceptions', { exception: { provider_id: leaveForm.provider_id, exception_type: Number(leaveForm.exception_type), start_date: leaveForm.start_date, end_date: leaveForm.end_date, reason: leaveForm.reason } }, opts(setSavingLeave, 'Added'))
  }
  const addCustom = () => {
    if (toMin(customForm.end_time) <= toMin(customForm.start_time)) { toast.error('End must be after start'); return }
    setSavingCustom(true)
    router.post('/settings/office-hours/exceptions', { exception: { provider_id: providerId, exception_type: 2, start_date: customForm.date, end_date: customForm.date, reason: customForm.reason, segments: [{ kind: 0, start_time: customForm.start_time, end_time: customForm.end_time }] } }, opts(setSavingCustom, 'One-off day added'))
  }
  const delException = (id) => router.delete(`/settings/office-hours/exceptions/${id}`, { preserveScroll: true, preserveState: true, onSuccess: () => toast.success('Removed'), onError: () => toast.error('Could not remove') })
  const doCopy = () => {
    if (!copyTo) { toast.error('Choose a provider to copy to'); return }
    if (!activeVersion?.id) { toast.error('No schedule to copy'); return }
    setCopying(true)
    router.post(`/settings/office-hours/versions/${activeVersion.id}/copy`, { to_provider_id: Number(copyTo), effective_from: copyEff }, opts(setCopying, 'Schedule copied'))
  }

  const leaveList = (oh.exceptions || []).filter(e => e.exception_type === 0 || e.exception_type === 1)
  const customList = (oh.exceptions || []).filter(e => e.exception_type === 2)
  const provName = (id) => id == null ? 'Practice-wide' : (providers.find(p => p.id === id)?.name || `#${id}`)

  const inputCls = 'h-9 rounded-lg border border-brand-border bg-brand-surface px-3 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary'
  const pillBtn = 'inline-flex items-center gap-2 rounded-xl bg-brand-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-primary/90 disabled:opacity-50'
  const ghostBtn = 'inline-flex items-center gap-1.5 rounded-lg border border-brand-border bg-white px-2.5 py-1.5 text-xs font-medium text-brand-muted hover:text-brand-ink hover:border-brand-primary/40'
  const fieldLabel = 'text-xs font-semibold uppercase tracking-wide text-brand-muted'

  return (
    <div className="space-y-6">
      {!oh.v2_enabled && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
          <p className="text-xs text-amber-800">Per-provider scheduling is currently OFF — changes here are saved but won't affect bookings until it's switched on.</p>
        </div>
      )}

      <Card icon={CalendarClock} title={t('settings_office_hours')} subtitle={t('settings_office_hours_desc')}>
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-brand-border bg-brand-surface p-1.5 w-fit">
          {realProviders.map(p => (
            <button key={p.id} onClick={() => switchProvider(p.id)}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${providerId === p.id ? 'bg-white text-brand-ink shadow-sm ring-1 ring-brand-border' : 'text-brand-muted hover:text-brand-ink'}`}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: p.color }} />
              {p.name}
            </button>
          ))}
        </div>

        <div className="mt-4 text-xs text-brand-muted">
          {isEditingFuture
            ? <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 px-2.5 py-1"><CalendarClock size={12} /> Editing an upcoming schedule (effective {activeVersion.effective_from})</span>
            : <span>Changes create a NEW schedule version, effective from{' '}
                <input type="date" value={effectiveFrom} min={tomorrowISO()} onChange={e => setEffectiveFrom(e.target.value)} className={`${inputCls} inline-block w-auto ml-1 h-8 py-1`} />
                {' '}— the current schedule stays until then.</span>}
        </div>

        <div className="mt-4 space-y-2">
          {DAY_ORDER.map(d => (
            <div key={d} className="rounded-xl border border-brand-border p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-brand-ink">{DAY_NAMES[d]}</span>
                {!dayHasWork(d)
                  ? <button onClick={() => addSeg(String(d), 0)} className={ghostBtn}><Plus size={12} /> Open this day</button>
                  : <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"><CheckCircle2 size={12} /> Open</span>}
              </div>
              {dayHasWork(d) && (
                <div className="space-y-1.5">
                  {week[String(d)].map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className={`text-[11px] font-semibold uppercase w-12 ${s.kind === 0 ? 'text-brand-primary' : 'text-amber-600'}`}>{s.kind === 0 ? 'Work' : 'Break'}</span>
                      <input type="time" value={s.start_time} onChange={e => updateSeg(String(d), i, 'start_time', e.target.value)} className={`${inputCls} w-28`} />
                      <span className="text-brand-muted">–</span>
                      <input type="time" value={s.end_time} onChange={e => updateSeg(String(d), i, 'end_time', e.target.value)} className={`${inputCls} w-28`} />
                      <button onClick={() => removeSeg(String(d), i)} className="text-brand-muted hover:text-red-500 p-1"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-0.5">
                    <button onClick={() => addSeg(String(d), 0)} className={ghostBtn}><Plus size={12} /> Work block</button>
                    <button onClick={() => addSeg(String(d), 1)} className={ghostBtn}><Plus size={12} /> Break</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {weekError && <p className="mt-3 text-xs text-red-600">{weekError}</p>}
        <div className="mt-4 flex items-center justify-between gap-3">
          <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Note (e.g. Post-maternity hours)" className={`${inputCls} w-64`} />
          <button onClick={saveWeek} disabled={savingWeek || !!weekError} className={pillBtn}><Save size={15} /> {savingWeek ? 'Saving…' : 'Save schedule'}</button>
        </div>
      </Card>

      <Card icon={Copy} title="Copy this week to another dentist" subtitle="Clone the current weekly template to another provider">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label className={fieldLabel}>To</label>
            <select value={copyTo} onChange={e => setCopyTo(e.target.value)} className={`${inputCls} w-48`}>
              <option value="">Choose dentist…</option>
              {realProviders.filter(p => p.id !== providerId).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={fieldLabel}>Effective from</label>
            <input type="date" value={copyEff} min={tomorrowISO()} onChange={e => setCopyEff(e.target.value)} className={`${inputCls} w-40`} />
          </div>
          <button onClick={doCopy} disabled={copying} className={pillBtn}><Copy size={15} /> {copying ? 'Copying…' : 'Copy'}</button>
        </div>
      </Card>

      <Card icon={CalendarOff} title="Leave & public holidays" subtitle="Close a dentist's diary (or the whole practice) for a date range">
        {leaveList.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-brand-border mb-4">
            <table className="min-w-full">
              <thead><tr className="bg-brand-surface">{['Dates', 'Type', 'Who', 'Reason', ''].map((h, i) => <th key={i} className="text-left px-4 py-2.5 text-xs font-semibold text-brand-muted uppercase tracking-wide">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-brand-border bg-white">
                {leaveList.map(e => (
                  <tr key={e.id} className="hover:bg-brand-surface/50">
                    <td className="px-4 py-2.5 text-sm text-brand-ink">{e.start_date}{e.end_date !== e.start_date ? ` – ${e.end_date}` : ''}</td>
                    <td className="px-4 py-2.5 text-sm text-brand-muted">{e.exception_type === 1 ? 'Public holiday' : 'Leave'}</td>
                    <td className="px-4 py-2.5 text-sm text-brand-muted">{provName(e.provider_id)}</td>
                    <td className="px-4 py-2.5 text-sm text-brand-muted">{e.reason}</td>
                    <td className="px-4 py-2.5 text-right"><button onClick={() => delException(e.id)} className="text-brand-muted hover:text-red-500"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5"><label className={fieldLabel}>Who</label>
            <select value={leaveForm.provider_id ?? ''} onChange={e => setLeaveForm(f => ({ ...f, provider_id: e.target.value === '' ? null : Number(e.target.value) }))} className={`${inputCls} w-44`}>
              {providers.map(p => <option key={p.id ?? 'all'} value={p.id ?? ''}>{p.name}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1.5"><label className={fieldLabel}>Type</label>
            <select value={leaveForm.exception_type} onChange={e => setLeaveForm(f => ({ ...f, exception_type: Number(e.target.value) }))} className={`${inputCls} w-36`}>
              <option value={0}>Leave</option><option value={1}>Public holiday</option>
            </select>
          </div>
          <div className="flex flex-col gap-1.5"><label className={fieldLabel}>From</label><input type="date" value={leaveForm.start_date} onChange={e => setLeaveForm(f => ({ ...f, start_date: e.target.value }))} className={inputCls} /></div>
          <div className="flex flex-col gap-1.5"><label className={fieldLabel}>To</label><input type="date" value={leaveForm.end_date} min={leaveForm.start_date} onChange={e => setLeaveForm(f => ({ ...f, end_date: e.target.value }))} className={inputCls} /></div>
          <div className="flex flex-col gap-1.5 flex-1 min-w-[10rem]"><label className={fieldLabel}>Reason</label><input type="text" value={leaveForm.reason} onChange={e => setLeaveForm(f => ({ ...f, reason: e.target.value }))} placeholder="e.g. Annual leave" className={inputCls} /></div>
          <button onClick={addLeave} disabled={savingLeave} className={pillBtn}><Plus size={15} /> Add</button>
        </div>
        {leaveForm.provider_id == null && <p className="mt-2 text-xs text-amber-700">⚠ Practice-wide closes the diary for every dentist on those dates.</p>}
      </Card>

      <Card icon={CalendarClock} title="One-off hours" subtitle="Special hours for a single date (e.g. a one-time Saturday)">
        {customList.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-brand-border mb-4">
            <table className="min-w-full">
              <thead><tr className="bg-brand-surface">{['Date', 'Hours', 'Who', 'Reason', ''].map((h, i) => <th key={i} className="text-left px-4 py-2.5 text-xs font-semibold text-brand-muted uppercase tracking-wide">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-brand-border bg-white">
                {customList.map(e => (
                  <tr key={e.id} className="hover:bg-brand-surface/50">
                    <td className="px-4 py-2.5 text-sm text-brand-ink">{e.start_date}</td>
                    <td className="px-4 py-2.5 text-sm text-brand-muted">{(e.segments || []).filter(s => s.kind === 0).map(s => `${s.start_time}–${s.end_time}`).join(', ')}</td>
                    <td className="px-4 py-2.5 text-sm text-brand-muted">{provName(e.provider_id)}</td>
                    <td className="px-4 py-2.5 text-sm text-brand-muted">{e.reason}</td>
                    <td className="px-4 py-2.5 text-right"><button onClick={() => delException(e.id)} className="text-brand-muted hover:text-red-500"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5"><label className={fieldLabel}>Date</label><input type="date" value={customForm.date} onChange={e => setCustomForm(f => ({ ...f, date: e.target.value }))} className={inputCls} /></div>
          <div className="flex flex-col gap-1.5"><label className={fieldLabel}>From</label><input type="time" value={customForm.start_time} onChange={e => setCustomForm(f => ({ ...f, start_time: e.target.value }))} className={`${inputCls} w-28`} /></div>
          <div className="flex flex-col gap-1.5"><label className={fieldLabel}>To</label><input type="time" value={customForm.end_time} onChange={e => setCustomForm(f => ({ ...f, end_time: e.target.value }))} className={`${inputCls} w-28`} /></div>
          <div className="flex flex-col gap-1.5 flex-1 min-w-[10rem]"><label className={fieldLabel}>Reason</label><input type="text" value={customForm.reason} onChange={e => setCustomForm(f => ({ ...f, reason: e.target.value }))} placeholder="e.g. Special Saturday" className={inputCls} /></div>
          <button onClick={addCustom} disabled={savingCustom} className={pillBtn}><Plus size={15} /> Add for {provName(providerId)}</button>
        </div>
      </Card>
    </div>
  )
}
